import { useEventStore } from "@/event";
import {
	getEventiumIconAssetsForType,
	type MapAssetPair,
} from "@/event/integration/game/eventMapAssetCatalog";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import Phaser from "phaser";

/** Ask MainMapScene to load seasonal map textures (safe if scene not ready yet). */
export function requestEventMapAssetsLoad(): void {
	useEmitter().emit(EmitterEvents.PhaserLoadEventMapAssets);
}

function queueEventiumIcon(
	scene: Phaser.Scene,
	key: string,
	{ uncompressed }: MapAssetPair,
): void {
	if (scene.textures.exists(key)) return;
	scene.load.image(key, uncompressed);
}

/**
 * Loads seasonal eventium icons when an event becomes active after map preload
 * (starts while the player is already in-game)
 */
export function ensureEventMapAssetsLoaded(scene: Phaser.Scene): Promise<void> {
	const { isActive, attributes } = useEventStore();
	if (!isActive) return Promise.resolve();

	const assets: Record<string, MapAssetPair> = getEventiumIconAssetsForType(
		attributes.type,
	);
	const pending: [string, MapAssetPair][] = Object.entries(assets).filter(
		([key]: [string, MapAssetPair]): boolean => !scene.textures.exists(key),
	);
	if (pending.length === 0) return Promise.resolve();

	for (const [key, pair] of pending) {
		queueEventiumIcon(scene, key, pair);
	}

	return new Promise(resolve => {
		scene.load.once(Phaser.Loader.Events.COMPLETE, (): void => resolve());
		if (!scene.load.isLoading()) {
			scene.load.start();
		}
	});
}
